import React from 'react';
import { type ReportingMonth } from '../lib/reportingMonth';
import {
  buildSubmissionDocId,
  fetchCrossAuditSubmissionsByAuditor,
  fetchCrossAuditSubmissionsFromDepartmentMonth,
  fetchDepartmentSubmissions,
  fetchUserSubmissionById,
  mergeCrossAuditDocLists,
  readDraftFromLocalStorageForScope,
  type FetchDepartmentSubmissionOptions,
  type SubmissionDocument,
} from '../services/submissions';
import { type RoleScope } from '../services/auth/roleResolver';
import { categorizeFirestoreError } from '../lib/firebaseErrors';
import {
  DEFAULT_NURSING_LAYOUT_TITLES,
  extractCrossAuditSlotFromDoc,
  findChartIndexByCrossAuditSlotWithFallback,
} from '../lib/crossAuditSlot';
import { mergeCrossAuditChartRowFromDoc } from '../lib/crossAuditMerge';
import { computeChartCountFromData, normalizeSubmissionShape } from '../services/submissionShape';
import { resolveCanonicalCategoryId } from '../lib/canonicalIds';
import { departmentIdScopeKey, getFirestorePathDepartmentId } from '../utils/normalizeIds';

const shouldDebugHydration = () =>
  import.meta.env.DEV || (typeof window !== 'undefined' && localStorage.getItem('debugHydration') === '1');

/** Counts only when `localStorage.debugHydration === '1'` — avoids noisy Vitest output in DEV. */
const shouldLogCrossAuditFetchDiag = () =>
  typeof window !== 'undefined' && localStorage.getItem('debugHydration') === '1';

/**
 * Ward used for user-path cross-audit scope checks vs hook `departmentId` (audited ward).
 * Legacy / alternate writers sometimes set `departmentId` to the auditor; dashboard code prefers
 * `auditedDepartmentId` for cross-audit rows — hydration must match or slices are dropped and
 * reload shows Submitted with empty 1/0/NA rows.
 */
const auditedWardIdForCrossAuditUserPathScope = (doc: SubmissionDocument): string | undefined => {
  if ((doc as { crossAudit?: unknown }).crossAudit === true) {
    const audited = (doc as { auditedDepartmentId?: unknown }).auditedDepartmentId;
    if (typeof audited === 'string' && audited.trim()) {
      return getFirestorePathDepartmentId(audited.trim()) ?? audited.trim();
    }
  }
  const raw = doc.departmentId;
  if (typeof raw === 'string' && raw.trim()) {
    return getFirestorePathDepartmentId(raw) ?? raw.trim();
  }
  return undefined;
};

/** Merge submitted arrays from local and firestore (OR) to avoid losing cross-audit progress on hydration */
const mergeSubmittedState = (
  firestore: SubmissionDocument,
  local: SubmissionDocument | null,
): void => {
  if (!local || !Array.isArray(local.submitted)) return;
  const fire = Array.isArray(firestore.submitted) ? (firestore.submitted as boolean[]) : [];
  const loc = local.submitted as boolean[];
  const len = Math.max(fire.length, loc.length);
  if (len === 0) return;
  const merged = Array.from({ length: len }, (_, i) => fire[i] || loc[i] || false);
  (firestore as { submitted?: boolean[] }).submitted = merged;
};

/** Merge cross-audit submitted state from Firestore into nursing draft (fallback when persistDepartmentDraft missed) */
/** Prefer the document that actually carries form rows (charts/answers) for the compliance side panel. */
const hydrationFormPayloadScore = (doc: SubmissionDocument): number => {
  const charts = Array.isArray((doc as { charts?: unknown[] }).charts)
    ? ((doc as { charts: unknown[] }).charts as unknown[]).length
    : 0;
  const { answers } = normalizeSubmissionShape(doc as Record<string, unknown>);
  const nonEmptyRows = answers.filter(
    (row) => Array.isArray(row) && row.some((c) => String(c ?? '').trim() !== ''),
  ).length;
  return charts * 10_000 + nonEmptyRows * 100 + answers.length;
};

type CrossAuditMergeStats = {
  appliedSlots: number;
  skippedNoSlot: number;
  skippedNoChartIndex: number;
};

/** Aggregated across merge passes in one hydration run (max per pass avoids double-counting trim re-merge). */
export type CrossAuditMergeDiagnostics = {
  skippedNoSlot: number;
  skippedNoChartIndex: number;
};

const mergeCrossAuditSubmittedState = (
  nursingData: SubmissionDocument,
  crossAuditDocs: readonly SubmissionDocument[],
): CrossAuditMergeStats => {
  const titles = Array.isArray(nursingData.chartTitles)
    ? (nursingData.chartTitles as string[])
    : [];
  const structuralChartCount = computeChartCountFromData(nursingData, 0);
  let submitted = Array.isArray(nursingData.submitted) ? [...(nursingData.submitted as boolean[])] : [];
  const existingDeptIds = Array.isArray((nursingData as { crossAuditByDeptIds?: unknown }).crossAuditByDeptIds)
    ? [...((nursingData as { crossAuditByDeptIds: string[] }).crossAuditByDeptIds)]
    : [];

  let appliedSlots = 0;
  let skippedNoSlot = 0;
  let skippedNoChartIndex = 0;

  for (const doc of crossAuditDocs) {
    const slot = extractCrossAuditSlotFromDoc(doc as Record<string, unknown>);
    if (!slot) {
      skippedNoSlot += 1;
      continue;
    }

    const idx = findChartIndexByCrossAuditSlotWithFallback(titles, slot, structuralChartCount);
    if (idx < 0) {
      skippedNoChartIndex += 1;
      continue;
    }

    appliedSlots += 1;
    while (submitted.length <= idx) submitted.push(false);
    submitted[idx] = true;

    const rawAud = (doc as { auditorDepartmentId?: unknown }).auditorDepartmentId;
    const rawLegacy = (doc as { crossAuditPerformedBy?: unknown }).crossAuditPerformedBy;
    const deptId =
      typeof rawAud === 'string' && rawAud.trim()
        ? rawAud.trim()
        : typeof rawLegacy === 'string' && rawLegacy.trim()
          ? rawLegacy.trim()
          : undefined;
    if (deptId) {
      while (existingDeptIds.length <= idx) existingDeptIds.push('');
      existingDeptIds[idx] = deptId;
    }

    if (shouldLogCrossAuditFetchDiag()) {
      const titleAtIdx = titles[idx];
      console.info('[Hydration] cross-audit slot → chart index', {
        crossAuditSlot: slot,
        chartIndex: idx,
        chartTitle: typeof titleAtIdx === 'string' ? titleAtIdx : null,
      });
    }

    mergeCrossAuditChartRowFromDoc(nursingData, idx, doc);
  }

  if (submitted.length > 0) {
    (nursingData as { submitted?: boolean[] }).submitted = submitted;
  }
  if (existingDeptIds.some(Boolean)) {
    (nursingData as { crossAuditByDeptIds?: string[] }).crossAuditByDeptIds = existingDeptIds;
  }

  return { appliedSlots, skippedNoSlot, skippedNoChartIndex };
};

/**
 * When no nurse_notes doc exists in Firestore maps but cross-audit slices do, merge still needs a
 * structural shell (chartTitles + parallel arrays) so slot indices resolve to ตรวจไขว้ rows.
 */
const createSyntheticNursingBasePayload = (category: string): SubmissionDocument => {
  const canonical = resolveCanonicalCategoryId(category);
  const n = DEFAULT_NURSING_LAYOUT_TITLES.length;
  const emptyStr = (): string => '';
  const emptyRow = (): string[] => [];
  return {
    category: canonical ?? category,
    categoryId: canonical ?? category,
    categoryTitle: category,
    chartTitles: [...DEFAULT_NURSING_LAYOUT_TITLES],
    answers: Array.from({ length: n }, emptyRow),
    focus: Array.from({ length: n }, emptyStr),
    treatments: Array.from({ length: n }, emptyStr),
    disease: Array.from({ length: n }, emptyStr),
    dates: Array.from({ length: n }, emptyStr),
    shifts: Array.from({ length: n }, emptyStr),
    hn: Array.from({ length: n }, emptyStr),
    suggestions: Array.from({ length: n }, emptyStr),
    problems: Array.from({ length: n }, emptyRow),
    submitted: Array.from({ length: n }, () => false),
    crossAuditByDeptIds: Array.from({ length: n }, emptyStr),
    incomplete: Array.from({ length: n }, () => false),
    draft: true,
  } as SubmissionDocument;
};

export const pickHydrationDepartment = (roleScope?: RoleScope | null) => {
  const departmentIds = roleScope?.view?.departmentIds ?? [];
  if (!departmentIds.length) return null;
  if (departmentIds.includes('*')) return null;
  return departmentIds[0] ?? null;
};

export interface SubmissionHydrationResult {
  loading: boolean;
  error: string | null;
  hydratedCategories: string[];
  retry: () => void;
  /**
   * When cross-audit docs were fetched but some slices could not be mapped to a chart (slot/title mismatch),
   * surfaces counts for UI/a11y. Null if no cross-audit batch ran or hydration did not complete.
   */
  crossAuditMergeDiagnostics: CrossAuditMergeDiagnostics | null;
}

interface SubmissionHydrationOptions {
  enabled: boolean;
  departmentId: string | null;
  reportingMonth: ReportingMonth;
  categories: string[];
  /**
   * `storageDepartmentId` must match the department used for Firestore reads in this hydration run.
   * If omitted, callers should treat it as "use current submission department" — the hook always passes
   * the run's `departmentId` so async completions cannot write fetched data under another ward's key.
   */
  makeMonthKey: (category: string, userId?: string | null, storageDepartmentId?: string | null) => string;
  onCategoryHydrated?: (category: string, data: SubmissionDocument) => void;
  userId?: string | null;
  fetchOptions?: FetchDepartmentSubmissionOptions;
  /** Categories to merge submitted state from localStorage (avoids losing cross-audit progress) */
  mergeWithLocalSubmitted?: string[];
  /** Additional department IDs to try for direct fetch when primary returns empty (e.g. HWN with multiple wards) */
  departmentIdsToTry?: string[];
}

/** Fetches department submissions, merges user vs dept sources, and writes to localStorage.
 * Hydration is idempotent: re-running overwrites localStorage with the same fetch result.
 * React Strict Mode double-invocation needs no extra handling.
 * TODO(nn-de-observability): Consider structured logging/metric for hydration selection
 * (category, chosenSubmissionId, source) in non-DEV for production support. */
export const useSubmissionHydration = ({
  enabled,
  departmentId,
  reportingMonth,
  categories,
  makeMonthKey,
  onCategoryHydrated,
  userId,
  fetchOptions,
  mergeWithLocalSubmitted = [],
  departmentIdsToTry = [],
}: SubmissionHydrationOptions): SubmissionHydrationResult => {
  const departmentIdsToTryRef = React.useRef(departmentIdsToTry);
  departmentIdsToTryRef.current = departmentIdsToTry;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [hydratedCategories, setHydratedCategories] = React.useState<string[]>([]);
  const [crossAuditMergeDiagnostics, setCrossAuditMergeDiagnostics] =
    React.useState<CrossAuditMergeDiagnostics | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);
  const makeMonthKeyRef = React.useRef(makeMonthKey);
  const onCategoryHydratedRef = React.useRef(onCategoryHydrated);
  const mergeWithLocalSubmittedRef = React.useRef(mergeWithLocalSubmitted);

  React.useEffect(() => {
    makeMonthKeyRef.current = makeMonthKey;
  }, [makeMonthKey]);

  React.useEffect(() => {
    onCategoryHydratedRef.current = onCategoryHydrated;
  }, [onCategoryHydrated]);

  React.useEffect(() => {
    mergeWithLocalSubmittedRef.current = mergeWithLocalSubmitted;
  }, [mergeWithLocalSubmitted]);

  // Reset hydrated state when month changes so we don't show stale data; hydration will re-fetch for the new month
  React.useEffect(() => {
    setHydratedCategories([]);
    setCrossAuditMergeDiagnostics(null);
  }, [reportingMonth]);

  React.useEffect(() => {
    if (!enabled) setCrossAuditMergeDiagnostics(null);
  }, [enabled]);

  const categoriesKey = React.useMemo(
    () =>
      categories
        .map((category) => category.trim())
        .filter((category): category is string => Boolean(category))
        .join('|'),
    [categories],
  );

  const uniqueCategories = React.useMemo(() => {
    if (!categoriesKey) return [];
    return Array.from(new Set(categoriesKey.split('|')));
  }, [categoriesKey]);

  const hydrate = React.useCallback(() => {
    if (!enabled || !departmentId || !uniqueCategories.length) {
      if (shouldDebugHydration()) {
        console.info('[Hydration] Skipped', { enabled, departmentId, uniqueCategoriesLength: uniqueCategories.length });
      }
      return undefined;
    }
    if (shouldDebugHydration()) {
      console.info('[Hydration] Starting', { departmentId, reportingMonth, uniqueCategories });
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCrossAuditMergeDiagnostics(null);

    const run = async () => {
      /** Must align localStorage keys with Firestore fetch target (stale runs after ward switch must not write into the newly selected key). */
      const storageDepartmentId = departmentId;
      try {
        const fetchMapWithPermissionFallback = async (
          fetcher: () => Promise<Map<string, SubmissionDocument | null>>,
        ): Promise<Map<string, SubmissionDocument | null>> => {
          try {
            return await fetcher();
          } catch (err) {
            const category = categorizeFirestoreError(err);
            if (category === 'permission-denied') {
              return new Map<string, SubmissionDocument | null>();
            }
            throw err;
          }
        };

        const preferServer = fetchOptions?.preferServer !== false;

        const logCrossAuditFetchCode = (label: string, err: unknown) => {
          if (!shouldLogCrossAuditFetchDiag()) return;
          const code =
            err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : 'unknown';
          console.info(`[Hydration] ${label} firestore code`, code);
        };

        const fetchCombinedCrossAuditDocs = async (): Promise<SubmissionDocument[]> => {
          if (!userId?.trim()) return [];
          let userPath: SubmissionDocument[] = [];
          let deptMirror: SubmissionDocument[] = [];
          try {
            userPath = await fetchCrossAuditSubmissionsByAuditor(userId, reportingMonth);
          } catch (err) {
            logCrossAuditFetchCode('fetchCrossAuditSubmissionsByAuditor', err);
            throw err;
          }
          try {
            deptMirror = await fetchCrossAuditSubmissionsFromDepartmentMonth(userId, departmentId, reportingMonth, {
              ...fetchOptions,
              preferServer,
            });
          } catch (err) {
            logCrossAuditFetchCode('fetchCrossAuditSubmissionsFromDepartmentMonth', err);
            throw err;
          }
          if (shouldLogCrossAuditFetchDiag()) {
            console.info('[Hydration] cross-audit fetch counts', {
              userPath: userPath.length,
              deptMirror: deptMirror.length,
            });
          }
          // Scope mismatch between hook `departmentId` (auditor / form path ward) and
          // `doc.departmentId` (audited ward on cross-audit slices) is expected. Department
          // mirror docs already live under submissions/{departmentId}/… so we must not drop
          // them here. Only filter the auditor user-path query, which can span wards.
          let userPathForMerge = userPath;
          if (departmentId?.trim()) {
            const targetDept =
              getFirestorePathDepartmentId(departmentId) ?? departmentId.trim();
            const targetScope = departmentIdScopeKey(targetDept);
            userPathForMerge = userPath.filter((doc) => {
              const docDept = auditedWardIdForCrossAuditUserPathScope(doc);
              if (!docDept?.trim()) return true;
              if (departmentIdScopeKey(docDept) !== targetScope && shouldLogCrossAuditFetchDiag()) {
                console.info('[Hydration] cross-audit user-path doc skipped (ward scope mismatch)', {
                  docDept,
                  targetDept,
                });
              }
              return departmentIdScopeKey(docDept) === targetScope;
            });
          }
          return mergeCrossAuditDocLists(userPathForMerge, deptMirror);
        };

        const [submissionMap, crossAuditDocs, deptMap] = await Promise.all([
          fetchMapWithPermissionFallback(() =>
            fetchDepartmentSubmissions(departmentId, reportingMonth, uniqueCategories, {
              submittedBy: userId,
              ...fetchOptions,
              preferServer: true, // Avoid stale cache after submit + refresh
            }),
          ),
          fetchCombinedCrossAuditDocs(),
          // Fallback: fetch department-level submissions (what supervisor sees) for categories
          // the current user hasn't submitted. Enables ward view to show submissions from
          // other ward nurses (e.g. Nurse A submitted, Nurse B views same ward).
          fetchMapWithPermissionFallback(() =>
            fetchDepartmentSubmissions(departmentId, reportingMonth, uniqueCategories, {
              ...fetchOptions,
              submittedBy: undefined,
              preferServer: true, // Avoid stale cache after submit + refresh
            }),
          ),
        ]);
        if (cancelled) return;

        if (shouldLogCrossAuditFetchDiag()) {
          const inRequested = (m: Map<string, SubmissionDocument | null>) =>
            uniqueCategories.filter((c) => m.has(c)).length;
          console.info('[Hydration] post-fetch category coverage', {
            crossAuditDocCount: crossAuditDocs.length,
            submissionMapHits: inRequested(submissionMap),
            deptMapHits: inRequested(deptMap),
            uniqueCategories: uniqueCategories.length,
          });
        }

        const hydrated: string[] = [];
        const mergeSet = new Set(
          (mergeWithLocalSubmittedRef.current ?? []).filter((s): s is string => typeof s === 'string' && s.trim() !== ''),
        );
        const categoryPrefersUserDoc = (cat: string): boolean => {
          if (mergeSet.has(cat)) return true;
          const canon = resolveCanonicalCategoryId(cat);
          return Boolean(canon && mergeSet.has(canon));
        };

        /**
         * Cross-audit row merge applies to nurse_notes even if merge-set strings drift (optional-gate).
         * Keep aligned with ComplianceHub `mergeWithLocalSubmittedList` (launch title + nursingCategoryDocId):
         * that list drives `categoryPrefersUserDoc`; this gate ensures canonical `nurse_notes` still merges
         * when the hydrated category string is only the doc id or Thai title.
         */
        const shouldMergeCrossAuditRows = (cat: string): boolean =>
          categoryPrefersUserDoc(cat) || resolveCanonicalCategoryId(cat) === 'nurse_notes';
        const missingCategories = uniqueCategories.filter((cat) => !submissionMap.has(cat));

        // For categories we don't have from current user, try to load full data from department
        // stub (submittedBy + submissionId) -> fetch full doc from users/{submittedBy}/submissions
        if (missingCategories.length > 0) {
          await Promise.all(
            missingCategories.map(async (category) => {
              const stub = deptMap.get(category);
              if (!stub) return;
              const submitterId = (stub.submittedBy ?? stub.userId) as string | undefined;
              const subId = (stub.submissionId ?? (stub as { id?: string }).id) as string | undefined;
              if (!submitterId || !subId) return;
              try {
                const fullDoc = await fetchUserSubmissionById(submitterId, subId);
                if (fullDoc && !cancelled) {
                  submissionMap.set(category, fullDoc);
                }
              } catch {
                /* ignore; stub may not have resolvable full doc */
              }
            }),
          );
        }
        if (cancelled) return;

        // Merge policy: when both user- and dept-scoped fetches return a doc for the same
        // category, pick one doc for localStorage. Each source already applied
        // pickLatestNonLegacyByCanonicalCategory per fetch.
        //
        // For categories in mergeWithLocalSubmitted (e.g. nursing), always prefer the current
        // user's document when both exist. Otherwise hydrationFormPayloadScore favors whichever
        // payload has more filled rows — the dept-path copy often still has the old chart count
        // after the user deleted charts from their draft, so reload would resurrect removed charts.
        const mergedMap = new Map<string, SubmissionDocument>();
        uniqueCategories.forEach((cat) => {
          const userDoc = submissionMap.get(cat);
          const deptDoc = deptMap.get(cat);
          if (!userDoc && !deptDoc) return;
          if (!userDoc) {
            mergedMap.set(cat, deptDoc!);
            return;
          }
          if (!deptDoc) {
            mergedMap.set(cat, userDoc);
            return;
          }
          if (categoryPrefersUserDoc(cat)) {
            mergedMap.set(cat, userDoc);
            return;
          }
          const pickDept = hydrationFormPayloadScore(deptDoc) > hydrationFormPayloadScore(userDoc);
          mergedMap.set(cat, pickDept ? deptDoc : userDoc);
        });

        // Direct fetch fallback: when both user and department fetches return empty for a category,
        // try users/{userId}/submissions/{docId} per docs/firestore-database-structure.md.
        // Doc ID format: {userId}_{scopeId}_{reportingMonth}_{category}
        const stillMissing = uniqueCategories.filter((cat) => !mergedMap.has(cat));
        const scopeIdsToTry = [
          departmentId,
          ...(departmentIdsToTryRef.current ?? []).filter((d) => d && d !== departmentId),
        ].filter(Boolean) as string[];
        if (stillMissing.length > 0 && userId && scopeIdsToTry.length > 0) {
          if (shouldDebugHydration()) {
            console.info('[Hydration] Direct fetch fallback', {
              reportingMonth,
              departmentId,
              scopeIdsToTry,
              stillMissing,
            });
          }
          await Promise.all(
            stillMissing.map(async (category) => {
              for (const scopeId of scopeIdsToTry) {
                if (cancelled || mergedMap.has(category)) break;
                try {
                  const docId = buildSubmissionDocId(userId, scopeId, reportingMonth, category);
                  let fullDoc = await fetchUserSubmissionById(userId, docId);
                  // Legacy docs may use category title in ID; try raw category if canonical id fails
                  if (!fullDoc) {
                    const altDocId = `${userId}_${scopeId}_${reportingMonth}_${category}`;
                    if (altDocId !== docId) {
                      fullDoc = await fetchUserSubmissionById(userId, altDocId);
                    }
                  }
                  if (fullDoc && !cancelled) {
                    mergedMap.set(category, fullDoc);
                    if (shouldDebugHydration()) {
                      console.info('[Hydration] Direct fetch found', { category, scopeId });
                    }
                    break;
                  }
                } catch {
                  /* ignore; direct fetch is best-effort fallback */
                }
              }
            }),
          );
        }
        if (cancelled) return;

        let mergeDiagMax: CrossAuditMergeDiagnostics | null = null;
        const recordMergeDiag = (stats: CrossAuditMergeStats) => {
          if (!mergeDiagMax) mergeDiagMax = { skippedNoChartIndex: 0, skippedNoSlot: 0 };
          mergeDiagMax.skippedNoChartIndex = Math.max(mergeDiagMax.skippedNoChartIndex, stats.skippedNoChartIndex);
          mergeDiagMax.skippedNoSlot = Math.max(mergeDiagMax.skippedNoSlot, stats.skippedNoSlot);
        };

        uniqueCategories.forEach((category) => {
          if (cancelled) return;

          const isSlice = extractCrossAuditSlotFromDoc({ category }) !== null;

          let data = mergedMap.get(category) ?? null;
          const fromSyntheticCrossAuditBase =
            !data &&
            crossAuditDocs.length > 0 &&
            shouldMergeCrossAuditRows(category) &&
            !isSlice;
          if (fromSyntheticCrossAuditBase) {
            data = createSyntheticNursingBasePayload(category);
            if (shouldLogCrossAuditFetchDiag()) {
              console.info('[Hydration] synthetic nurse_notes base for cross-audit-only merge', { category });
            }
          }
          if (!data) return;

          const monthKey = makeMonthKeyRef.current(category, userId, storageDepartmentId);
          const userPick = submissionMap.get(category);
          const deptPick = deptMap.get(category);
          let source:
            | 'user'
            | 'dept'
            | 'direct'
            | 'cross-audit-synthetic' =
            userPick && deptPick
              ? categoryPrefersUserDoc(category)
                ? 'user'
                : hydrationFormPayloadScore(deptPick) > hydrationFormPayloadScore(userPick)
                  ? 'dept'
                  : 'user'
              : userPick
                ? 'user'
                : deptPick
                  ? 'dept'
                  : 'direct';
          if (fromSyntheticCrossAuditBase) source = 'cross-audit-synthetic';
          const submissionId = (data.submissionId ?? (data as { id?: string }).id) as string | undefined;

          if (categoryPrefersUserDoc(category)) {
            const localDraft = readDraftFromLocalStorageForScope(monthKey, storageDepartmentId);
            mergeSubmittedState(data, localDraft);
            // Fallback: merge cross-audit submitted state (chart 1,2,3) when persistDepartmentDraft missed
            if (crossAuditDocs.length > 0 && !isSlice) {
              const mergeStats = mergeCrossAuditSubmittedState(data, crossAuditDocs);
              recordMergeDiag(mergeStats);
              if (shouldLogCrossAuditFetchDiag()) {
                console.info('[Hydration] cross-audit merge stats', { category, ...mergeStats });
              }
            }
            // User doc from Firestore can lag behind localStorage after chart deletes (persist in flight).
            // mergeSubmittedState only ORs `submitted[]`; without this, we would write the stale server
            // payload to localStorage and deleted charts reappear after reload.
            if (localDraft) {
              const localCount = computeChartCountFromData(localDraft, 0);
              const serverCount = computeChartCountFromData(data, 0);
              const isDraft = (localDraft as { draft?: boolean }).draft !== false;
              if (isDraft && localCount < serverCount) {
                const serverSnapshot = JSON.parse(JSON.stringify(data)) as SubmissionDocument;
                const trimIdx = localCount;
                if (Array.isArray((serverSnapshot as { submitted?: unknown[] }).submitted)) {
                  (serverSnapshot as { submitted?: boolean[] }).submitted = (
                    serverSnapshot as { submitted: boolean[] }
                  ).submitted.slice(0, trimIdx);
                }
                if (Array.isArray((serverSnapshot as { crossAuditByDeptIds?: unknown[] }).crossAuditByDeptIds)) {
                  (serverSnapshot as { crossAuditByDeptIds?: string[] }).crossAuditByDeptIds = (
                    serverSnapshot as { crossAuditByDeptIds: string[] }
                  ).crossAuditByDeptIds.slice(0, trimIdx);
                }
                Object.assign(data, localDraft);
                mergeSubmittedState(data, serverSnapshot);
                if (crossAuditDocs.length > 0 && !isSlice) {
                  const mergeStatsTrim = mergeCrossAuditSubmittedState(data, crossAuditDocs);
                  recordMergeDiag(mergeStatsTrim);
                  if (shouldLogCrossAuditFetchDiag()) {
                    console.info('[Hydration] cross-audit merge stats (after trim)', {
                      category,
                      ...mergeStatsTrim,
                    });
                  }
                }
              }
            }
          } else if (
            crossAuditDocs.length > 0 &&
            shouldMergeCrossAuditRows(category) &&
            !isSlice
          ) {
            const mergeStatsElse = mergeCrossAuditSubmittedState(data, crossAuditDocs);
            recordMergeDiag(mergeStatsElse);
            if (shouldLogCrossAuditFetchDiag()) {
              console.info('[Hydration] cross-audit merge stats', { category, ...mergeStatsElse });
            }
          }

          try {
            localStorage.setItem(monthKey, JSON.stringify(data));
            if (shouldDebugHydration()) {
              const isNN = category === 'บันทึกทางการพยาบาล' || category === 'nurse_notes';
              console.info('[Hydration] Wrote to localStorage', {
                category,
                departmentId,
                reportingMonth,
                source,
                submissionId: submissionId ?? null,
                submittedAt: (data.submittedAt as unknown) ?? null,
                legacy: (data as { legacy?: boolean }).legacy ?? null,
                monthKey: monthKey.slice(0, 60) + '...',
                ...(isNN ? { nnSelected: true } : {}),
              });
            }
          } catch {
            /* ignore storage failures */
          }

          hydrated.push(category);
          onCategoryHydratedRef.current?.(category, data);
        });

        setHydratedCategories(hydrated);
        setCrossAuditMergeDiagnostics(
          crossAuditDocs.length > 0 && mergeDiagMax ? mergeDiagMax : null,
        );
      } catch (err) {
        const category = categorizeFirestoreError(err);
        // #region agent log
        fetch('http://127.0.0.1:7420/ingest/37c278d6-077c-403a-b2af-3f64b2badb71', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '241961' },
          body: JSON.stringify({
            sessionId: '241961',
            runId: 'pre-fix',
            hypothesisId: 'B',
            location: 'useSubmissionHydration.ts:catch',
            message: 'Hydration failed',
            data: {
              departmentId,
              reportingMonth,
              uniqueCategories,
              errCategory: category,
              errMsg: err instanceof Error ? err.message : String(err),
              slashCategories: uniqueCategories.filter((c) => c.includes('/')),
            },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion

        if (category !== 'permission-denied') {
          console.error('Failed to hydrate saved submissions', err);
        }

        if (!cancelled) {
          setCrossAuditMergeDiagnostics(null);
          if (category === 'network') {
            setError('ไม่สามารถโหลดข้อมูลที่บันทึกไว้ (ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต)');
          } else if (category === 'permission-denied') {
            setError(null); // Common case for users without access, not a true error
          } else {
            setError('ไม่สามารถโหลดข้อมูลที่บันทึกไว้');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
        else setCrossAuditMergeDiagnostics(null);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [departmentId, enabled, fetchOptions, reportingMonth, uniqueCategories, userId]);

  React.useEffect(() => {
    const cleanup = hydrate();
    return () => {
      cleanup?.();
    };
  }, [enabled, hydrate, reloadToken, uniqueCategories]);

  const retry = React.useCallback(() => {
    setHydratedCategories([]);
    setCrossAuditMergeDiagnostics(null);
    setReloadToken((token) => token + 1);
  }, []);

  React.useDebugValue(
    { loading, hydratedCount: hydratedCategories.length, crossAuditMergeDiagnostics },
    (s) => {
      if (s.loading) return 'hydration:loading';
      if (!s.hydratedCount) return 'hydration:idle';
      const d = s.crossAuditMergeDiagnostics;
      const ca =
        d && (d.skippedNoSlot > 0 || d.skippedNoChartIndex > 0)
          ? `:ca-skip(slot=${d.skippedNoSlot},idx=${d.skippedNoChartIndex})`
          : '';
      return `hydration:done(${s.hydratedCount})${ca}`;
    },
  );

  return { loading, error, hydratedCategories, retry, crossAuditMergeDiagnostics };
};
